// askSmart-ui/src/moduleBranding.ts
//
// Which icon the browser tab shows, per module.
//
// Matched on the PATHNAME by substring, not by exact route. Module routes come
// from the module catalog and an admin can rename them, which is why the rest of
// the app identifies modules the same loose way — `isVerityModule` in App.js,
// `isTosp` and `isMapleCoin` in ModulePlaceholder.tsx. An exact-path table here
// would silently fall back to the AskSmart icon the day somebody edits a route.
//
// One entry covers both sides of a module: "tosp" matches /tosp and /managetosp,
// "maplecoin" matches /maplecoin and /manage-maplecoin, and so on. Nothing needs
// to list the admin variants separately.

export interface ModuleBrand {
  /** Shown in the browser tab and used for bookmarks. Served from /public. */
  favicon: string;
  /**
   * The browser tab's text, and what a bookmark is named.
   *
   * The module's own name, not "AskSmart — CaddieGames": a user inside
   * CaddieGames is in CaddieGames, and the platform behind it is not what the
   * tab is for. Cased as the module brands itself — "myTOSP", "IQVerity".
   */
  title: string;
  /**
   * Substrings that identify the module in a URL path, lower-cased.
   *
   * Order matters across the whole table: the FIRST module with a match wins, so
   * a narrower name must come before a broader one that contains it.
   */
  match: string[];
}

/** The platform default — what every route that isn't a module falls back to. */
export const ASKSMART_FAVICON = "/AskSmart.png";
export const ASKSMART_TITLE = "AskSmart";

export const moduleBranding: Record<string, ModuleBrand> = {
  caddiegames: {
    // The square app icon, not CaddieGamesLogo.png — that one is a 1448x1086
    // wordmark and letterboxes to nothing legible at 16px.
    favicon: "/CaddieGames Icon.png",
    title: "CaddieGames",
    match: ["caddiegames"],
  },
  iqverity: {
    favicon: "/IQVerity_appicon.png",
    title: "IQVerity",
    // The catalog name and the routes both use "verity"; "iqverity" is listed
    // first so either spelling resolves.
    match: ["iqverity", "verity"],
  },
  tosp: {
    favicon: "/myTOSP_Icon.png",
    title: "myTOSP",
    match: ["tosp"],
  },
  maplecoin: {
    // A .jpg where the rest are .png — the hook sets the link's type to match,
    // because a strict browser ignores a JPEG declared as image/png.
    favicon: "/Maplecoin logo.jpg",
    title: "MapleCoin",
    match: ["maplecoin", "maple"],
  },
  asksmart: {
    favicon: ASKSMART_FAVICON,
    title: ASKSMART_TITLE,
    match: [],
  },
};

/**
 * The branding for a path, or AskSmart's where no module claims it.
 *
 * Returns the whole brand rather than one field at a time, so the icon and the
 * title are decided by a single match and cannot end up describing two different
 * modules.
 *
 * Case-insensitive, because a catalog route is typed by hand.
 */
export function brandForPath(pathname: string): ModuleBrand {
  const path = (pathname || "").toLowerCase();
  for (const brand of Object.values(moduleBranding)) {
    if (brand.match.some((token) => path.includes(token))) return brand;
  }
  return moduleBranding.asksmart;
}
